import { useEffect, useMemo } from 'react';
import {
  Button,
  Menu,
  Stack,
  Header,
  MenuButton
} from '@efast_public/fjd-component-library';

import { useProfile } from '../../shared/hooks/AuthContext';
import { useNavigate } from 'react-router-dom';
import useConfigurationStep, {
  ConfigurationStep
} from '../../api/hooks/useConfigurationStep';
import {
  createDataUrlFromLogoVO,
  logoState,
  LogoVO
} from '../../api/hooks/useLogo';

import style from './Header.module.css';
import { useQuery } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../api/beh-api';
import { useRecoilState } from 'recoil';
import { ProductResponse } from '../../api/govos.types';
import { RoutePaths } from '../../pages/AppRoutes';
import { GovosApiRoutes } from 'api/govos-api';
import { govosClient } from '../../api/govos-api';

export function BehHeader() {
  const { profile, hasRole, logout: profileLogout } = useProfile(); // Error handling does not look good, all known error cases are catched via route
  // TODO: only a temporary solution, otherwise the logo is not updated after deleting the image
  const [shouldRefetch, setShouldRefetch] = useRecoilState(logoState);
  const { configurationStep } = useConfigurationStep();

  const { data: logoVO, refetch } = useQuery<LogoVO | null>({
    queryKey: [BehApiRoutes.logo()],
    queryFn: () =>
      behClient
        .get<LogoVO>(BehApiRoutes.logo())
        .then((res) => res.data)
        .catch(() => null),
    staleTime: 0
  });

  const { data: productResponse } = useQuery<ProductResponse>({
    queryKey: [GovosApiRoutes.products()],
    queryFn: () =>
      govosClient.get(GovosApiRoutes.products()).then((res) => res.data),
    enabled: false
  });
  const products = productResponse?.userInfo?.products ?? [];

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [shouldRefetch, refetch, setShouldRefetch]);

  const hasGroupAdministration = useMemo(
    () => hasRole('Administration'),
    [hasRole]
  );

  const navigate = useNavigate();

  const logout = () => {
    profileLogout();
  };

  const isInConfigAssistantMode: boolean = configurationStep
    ? configurationStep.configurationStep !== ConfigurationStep.FINISH
    : false;

  const showSettings =
    profile && hasGroupAdministration && !isInConfigAssistantMode;

  return (
    <Header
      logo={
        <Stack direction="row" spacing="s" alignItems="center">
          {products.length > 0 && (
            <MenuButton variant="primary-text" icon="grid">
              <Menu>
                {products?.map((product) => (
                  <Menu.MenuItem
                    key={product.name}
                    label={product.name}
                    icon={`product-${product.identifier}`}
                    onClick={() => {
                      window.open(product.url, '_blank', 'noopener,noreferrer');
                    }}
                  />
                ))}
              </Menu>
            </MenuButton>
          )}
          <Button
            variant="primary-text"
            beforeIcon="product-beh"
            size="md"
            onClick={() => navigate(RoutePaths.HOME)}
          >
            Behörden-Client
          </Button>
          {logoVO && (
            <img
              src={createDataUrlFromLogoVO(logoVO)}
              className={`${style.headerLogos} ${style.clientLogo}`}
              alt="Logo Mandant"
            />
          )}
        </Stack>
      }
      navigation={
        <Stack direction="row" spacing="s" alignItems="center">
          {showSettings && (
            <Button
              variant="primary-text"
              beforeIcon="settings"
              size="md"
              id={'settings-link'}
              onClick={() => navigate('/einstellungen')}
            >
              Einstellungen
            </Button>
          )}
          {profile && (
            <MenuButton
              variant="primary-text"
              icon="user-avatar"
              label={profile?.name}
              type="button"
              size="md"
            >
              <Menu.MenuItem
                data-testid="buttonLogout"
                icon="logout"
                label={'Abmelden'}
                onClick={() => {
                  logout();
                }}
              />
            </MenuButton>
          )}
        </Stack>
      }
    />
  );
}
